import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Printer,
  Package,
  AlertCircle,
  AlertTriangle,
  Search,
  FileText,
} from 'lucide-react';

import { Breadcrumb } from '../components/Breadcrumb';
import { SalesOrderShipmentsTable } from '../components/salesOrders/SalesOrderShipmentsTable';
import { ShipCompleteWarnings } from '../components/salesOrders/ShipCompleteWarnings';
import { apiService } from '../services/apiService';
import { getCachedData, setCachedData } from '../utils/pageCache';
import type {
  SalesOrder,
  SalesOrderLine,
  SalesOrderShipment,
} from '../services/types';
import { parseCalendarDate } from '../utils/calendarDate';

/**
 * Helper function to format date string to a more readable format
 */
function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const date = parseCalendarDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-CA');
}

/**
 * Helper function to format price with currency
 */
function formatPrice(value: number, currency: string): string {
  const formatted = value.toFixed(2);
  const [dollars, cents] = formatted.split('.');
  const dollarsParts: string[] = [];
  let count = 0;
  for (let i = dollars.length - 1; i >= 0; i--) {
    if (count === 3) {
      dollarsParts.unshift(' ');
      count = 0;
    }
    dollarsParts.unshift(dollars[i]);
    count++;
  }
  return `${dollarsParts.join('')}.${cents} ${currency}`;
}

/**
 * Get status badge color based on order/line status
 */
function getStatusBadge(
  openOrder: boolean,
  voidOrder: boolean,
  orderHeld: boolean
): { text: string; className: string } {
  if (voidOrder)
    return { text: 'Void', className: 'bg-gray-100 text-gray-800' };
  if (orderHeld)
    return { text: 'On Hold', className: 'bg-yellow-100 text-yellow-800' };
  if (openOrder)
    return { text: 'Open', className: 'bg-green-100 text-green-800' };
  return { text: 'Closed', className: 'bg-blue-100 text-blue-800' };
}

function getLineStatusBadge(openLine: boolean): {
  text: string;
  className: string;
} {
  if (!openLine) {
    return { text: 'Closed', className: 'bg-blue-100 text-blue-800' };
  }
  return { text: 'Open', className: 'bg-green-100 text-green-800' };
}

/**
 * Filter out the name from the address to avoid duplication
 * The address string often contains the name as the first line
 */
function filterNameFromAddress(address: string, name: string): string {
  if (!address || !name) return address || '';
  const lines = address.replace(/\\r\\n/g, '\n').split('\n');
  const filteredLines = lines.filter(
    (line) => line.trim().toLowerCase() !== name.trim().toLowerCase()
  );
  return filteredLines.join('\n');
}

export const SalesOrderTrackerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderNum: orderNumParam } = useParams<{ orderNum: string }>();
  const [salesOrder, setSalesOrder] = useState<SalesOrder | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isPickListLoading, setIsPickListLoading] = useState(false);
  const [shipments, setShipments] = useState<SalesOrderShipment[]>([]);
  const [isShipmentsLoading, setIsShipmentsLoading] = useState(false);
  const [shipmentsError, setShipmentsError] = useState<string | null>(null);

  const fetchSalesOrder = useCallback(async (orderNum: string) => {
    const orderNumInt = parseInt(orderNum, 10);
    if (isNaN(orderNumInt)) {
      setError('Invalid order number');
      setIsLoading(false);
      return;
    }

    // Check cache first
    const cacheKey = `salesOrder_${orderNum}`;
    const cached = getCachedData<SalesOrder>(cacheKey);
    if (cached) {
      setSalesOrder(cached);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getSalesOrder(orderNumInt);
      setSalesOrder(data);
      // Cache the result
      setCachedData(cacheKey, data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch sales order'
      );
      setSalesOrder(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Shipments load separately so a shipments failure doesn't hide the order
  const fetchShipments = useCallback(async (orderNum: string) => {
    const orderNumInt = parseInt(orderNum, 10);
    if (isNaN(orderNumInt)) {
      setShipments([]);
      return;
    }

    const cacheKey = `salesOrderShipments_${orderNum}`;
    const cached = getCachedData<SalesOrderShipment[]>(cacheKey);
    if (cached) {
      setShipments(cached);
      setShipmentsError(null);
      setIsShipmentsLoading(false);
      return;
    }

    try {
      setIsShipmentsLoading(true);
      setShipmentsError(null);
      const data = await apiService.getSalesOrderShipments(orderNumInt);
      setShipments(data);
      setCachedData(cacheKey, data);
    } catch (err) {
      setShipmentsError(
        err instanceof Error ? err.message : 'Failed to fetch shipments'
      );
      setShipments([]);
    } finally {
      setIsShipmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (orderNumParam) {
      setSearchValue(orderNumParam);
      fetchSalesOrder(orderNumParam);
      fetchShipments(orderNumParam);
    }
  }, [orderNumParam, fetchSalesOrder, fetchShipments]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      // Update URL and fetch data - encode to handle special characters like "/"
      navigate(`/sales-orders/${encodeURIComponent(searchValue.trim())}`);
    }
  };

  // Get shipping label ID - use ShipToNum if available, otherwise custId
  const getShippingLabelId = (): string => {
    if (!salesOrder) return '';
    const { shipToNum, custId } = salesOrder.header;
    return shipToNum || custId;
  };

  // Handle downloading pick list PDF
  const handleDownloadPickList = async () => {
    if (!salesOrder) return;
    try {
      setIsPickListLoading(true);
      const { presignedUrl } = await apiService.getPickListPdf(
        salesOrder.header.orderNum
      );
      window.open(presignedUrl, '_blank');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to generate pick list PDF'
      );
    } finally {
      setIsPickListLoading(false);
    }
  };

  const { header, lines } = salesOrder || { header: null, lines: [] };
  const statusBadge = header
    ? getStatusBadge(header.openOrder, header.voidOrder, header.orderHeld)
    : null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('salesOrder.tracker')} />

        {/* Header */}
        <div className="mb-8 mt-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('salesOrder.tracker')}
            </h1>
            <p className="mt-2 text-gray-600">
              {t(
                'salesOrder.trackerDescription',
                'View and track sales order details'
              )}
            </p>
          </div>
          {salesOrder && (
            <div className="flex gap-2">
              <button
                onClick={handleDownloadPickList}
                disabled={isPickListLoading}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-10 px-4 bg-amber-600 text-white hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FileText className="w-4 h-4" />
                {isPickListLoading
                  ? t('common.loading', 'Loading...')
                  : t('salesOrder.printPickList', 'Print Pick List')}
              </button>
              <Link
                to={`/label-printing?type=shipping&id=${encodeURIComponent(getShippingLabelId())}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-10 px-4 bg-blue-600 text-white hover:bg-blue-700"
              >
                <Package className="w-4 h-4" />
                {t('salesOrder.printShippingLabel')}
              </Link>
            </div>
          )}
        </div>

        {/* Search Component */}
        <div className="space-y-6">
          {/* Search Input with Button */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t(
                  'salesOrder.searchPlaceholder',
                  'Enter sales order number (e.g., 12345)'
                )}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                disabled={isLoading}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isLoading || !searchValue.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {isLoading
                ? t('parts.search.searching', 'Searching...')
                : t('common.search', 'Search')}
            </button>
          </div>

          {/* Error Message */}
          {error && !isLoading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900">
                  {t('common.error', 'Error')}
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                {t('parts.search.searching', 'Searching...')}...
              </p>
            </div>
          )}

          {/* Initial State - Prompting for Search */}
          {!salesOrder && !isLoading && !error && (
            <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
              <Search className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <p className="text-blue-900 font-medium">
                {t(
                  'salesOrder.enterOrderNumber',
                  'Enter a sales order number above to view order details'
                )}
              </p>
              <p className="text-blue-700 text-sm mt-1">
                {t(
                  'salesOrder.searchInstructions',
                  'Search by sales order number to view order header and line details'
                )}
              </p>
            </div>
          )}

          {/* Order Content */}
          {salesOrder && header && !isLoading && (
            <>
              <ShipCompleteWarnings salesOrder={salesOrder} />

              {/* Order Header Section */}
              <div className="bg-white rounded border border-gray-300 shadow-sm">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-blue-900">
                      {t('salesOrder.orderHeader', 'Order Header')}
                    </h2>
                    <p className="text-xs text-blue-700">
                      {header.customerName} ({header.custId})
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {header.creditHold && (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        Credit Hold
                      </span>
                    )}
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge?.className}`}
                    >
                      {statusBadge?.text}
                    </span>
                  </div>
                </div>

                <div className="p-4 space-y-6">
                  {/* Three Column Layout: Ship To, Bill To, General Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Ship To Section */}
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h3 className="text-sm font-bold text-green-900 uppercase tracking-tight mb-3">
                        {t('salesOrder.shipTo', 'Ship To')}
                      </h3>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {header.shipToName || '—'}
                          {header.shipToNum && (
                            <span className="text-gray-600 font-normal">
                              {' '}
                              ({header.shipToNum})
                            </span>
                          )}
                        </p>
                        {header.shipToAddress && (
                          <p className="text-xs text-gray-700 whitespace-pre-line">
                            {filterNameFromAddress(
                              header.shipToAddress,
                              header.shipToName
                            )}
                          </p>
                        )}
                        {header.shipToContactName && (
                          <p className="text-xs text-gray-700">
                            <span className="font-medium">Contact:</span>{' '}
                            {header.shipToContactName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Bill To Section */}
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h3 className="text-sm font-bold text-purple-900 uppercase tracking-tight mb-3">
                        {t('salesOrder.billTo', 'Bill To')}
                      </h3>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {header.btCustName || header.customerName || '—'}
                          {header.btCustId && (
                            <span className="text-gray-600 font-normal">
                              {' '}
                              ({header.btCustId})
                            </span>
                          )}
                        </p>
                        {header.btAddress && (
                          <p className="text-xs text-gray-700 whitespace-pre-line">
                            {filterNameFromAddress(
                              header.btAddress,
                              header.btCustName || header.customerName
                            )}
                          </p>
                        )}
                        {header.btContactName && (
                          <p className="text-xs text-gray-700">
                            <span className="font-medium">Contact:</span>{' '}
                            {header.btContactName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* General Info Section */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-tight mb-3">
                        {t('salesOrder.generalInfo', 'General Info')}
                      </h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.poNumber', 'PO #')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.poNum || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.orderDate', 'Order Date')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.orderDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.needByDate', 'Need By')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.needByDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.requestDate', 'Request')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.requestDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.shipVia', 'Ship Via')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.shipViaDescription || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.entryPerson', 'Entry')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.entryPerson || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.salesRep', 'Sales Rep')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.salesRepName || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('salesOrder.totalLines', 'Lines')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.totalLines}
                          </span>
                        </div>
                        <div className="col-span-2 pt-2 border-t border-gray-200 mt-2">
                          <span className="text-gray-600">
                            {t('salesOrder.totalCharges', 'Total')}:
                          </span>
                          <span className="ml-1 font-bold text-gray-900">
                            {formatPrice(
                              header.docTotalCharges - header.docTotalDiscount,
                              header.currencyCode
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comments */}
                  {(header.orderComment || header.shipComment) && (
                    <div className="pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {header.orderComment && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                            {t('salesOrder.orderComment')}
                          </p>
                          <p className="text-sm text-gray-700">
                            {header.orderComment}
                          </p>
                        </div>
                      )}
                      {header.shipComment && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                            {t('salesOrder.shipComment')}
                          </p>
                          <p className="text-sm text-gray-700">
                            {header.shipComment}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Order Lines Section */}
              <div className="bg-white rounded border border-gray-300 shadow-sm">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                  <h2 className="text-lg font-bold text-blue-900">
                    {t('salesOrder.orderLines')} ({lines.length})
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.line')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.partNum')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.description')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.qty')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.shipped')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.onHand')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.basePrice')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.discount')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.unitPrice')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('salesOrder.status')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('parts.search.action')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line: SalesOrderLine) => {
                        const lineStatus = getLineStatusBadge(line.openLine);
                        // Use CustPartNum if available for print label, else use PartNumUM
                        const printPartId =
                          line.custPartNum || line.partNumUM || line.partNum;
                        const hasCustPartNum = !!line.custPartNum;

                        return (
                          <tr
                            key={line.orderLine}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-gray-900 font-medium">
                              {line.orderLine}
                            </td>
                            <td className="px-4 py-3">
                              {/* PartNumUM in blue, linking to /parts/{partNum} */}
                              <div className="flex flex-col items-start gap-1">
                                <Link
                                  to={`/parts/${encodeURIComponent(line.partNum)}`}
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                >
                                  {line.partNumUM || line.partNum}
                                </Link>
                                {/* Show CustPartNum with warning icon if available */}
                                {hasCustPartNum && (
                                  <div className="flex items-center gap-1">
                                    <span
                                      className="text-red-500 font-bold text-lg leading-none"
                                      title={`Customer Part: ${line.custPartNum}`}
                                      aria-label={`Customer part indicator: ${line.custPartNum}`}
                                    >
                                      ⚠
                                    </span>
                                    <span className="text-xs text-gray-600">
                                      {line.custPartNum}
                                    </span>
                                  </div>
                                )}
                                {line.shipLineComplete && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <AlertTriangle className="w-3 h-3" />
                                    {t('salesOrder.shipLineComplete')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700 max-w-xs truncate">
                              {line.lineDesc}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900">
                              {Math.round(line.orderQty)} {line.ium}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900">
                              {Math.round(line.totalShipped)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900">
                              {Math.round(line.onHandQty)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900">
                              {formatPrice(
                                line.docUnitPrice,
                                header.currencyCode
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900">
                              {line.discountPercent
                                ? `${line.discountPercent}%`
                                : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              {formatPrice(
                                line.docOrderBasedPrice,
                                header.currencyCode
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${lineStatus.className}`}
                              >
                                {lineStatus.text}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Link
                                to={`/label-printing?type=external-part&part=${encodeURIComponent(printPartId)}`}
                                className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-xs font-medium transition-colors h-8 px-3 bg-green-600 text-white hover:bg-green-700"
                                title={t('salesOrder.printPartLabel')}
                              >
                                <Printer className="w-3 h-3" />
                                {t('labelPrinting.print')}
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {lines.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      {t('salesOrder.noLines')}
                    </div>
                  )}
                </div>
              </div>

              {/* Shipments Section */}
              <SalesOrderShipmentsTable
                shipments={shipments}
                isLoading={isShipmentsLoading}
                error={shipmentsError}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
